// AuditLog.jsx — Admin-only activity log viewer with filters + CSV export.
import React, { useEffect, useState, useCallback } from "react";
import { api, formatErr } from "./lib/api";
import { downloadCSV } from "./lib/export";
import { downloadPDF } from "./lib/pdf";
import { formatKSADate } from "./lib/ksaTime";

const EVENT_LABELS = {
  "login.success": { lbl: "🔓 دخول ناجح", color: "var(--success)" },
  "login.failed": { lbl: "❌ دخول فاشل", color: "var(--warn)" },
  "login.locked": { lbl: "🔒 حساب مقفل", color: "var(--danger)" },
  "logout": { lbl: "🚪 خروج", color: "var(--info)" },
  "user.created": { lbl: "➕ إنشاء مستخدم", color: "var(--success)" },
  "user.updated": { lbl: "✏ تعديل مستخدم", color: "var(--info)" },
  "user.deleted": { lbl: "🗑 حذف مستخدم", color: "var(--danger)" },
  "data.cleared": { lbl: "🧹 مسح بيانات", color: "var(--danger)" },
  "data.cleared_all": { lbl: "💥 مسح كل البيانات", color: "var(--danger)" },
};

function eventChip(ev) {
  const cfg = EVENT_LABELS[ev] || { lbl: ev, color: "var(--muted)" };
  return (
    <span style={{ background: cfg.color, color: "#fff", padding: "2px 8px", borderRadius: 4, fontSize: 11, whiteSpace: "nowrap" }}>
      {cfg.lbl}
    </span>
  );
}

function formatMeta(meta) {
  if (!meta || Object.keys(meta).length === 0) return "—";
  return Object.entries(meta)
    .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
    .join(" · ");
}

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [eventFilter, setEventFilter] = useState("");
  const [usernameFilter, setUsernameFilter] = useState("");
  const [limit, setLimit] = useState(100);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (eventFilter) params.set("event", eventFilter);
      if (usernameFilter) params.set("username", usernameFilter);
      const { data } = await api.get(`/auth/audit-logs?${params}`);
      setItems(data.items || []);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, [eventFilter, usernameFilter, limit]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!items.length) return;
    const header = ["التوقيت", "الحدث", "المستخدم", "الدور", "IP", "التفاصيل"];
    const rows = items.map((r) => [
      (r.ts || "").replace("T", " ").slice(0, 19),
      r.event, r.username || "—", r.role || "—",
      r.ip || "—", formatMeta(r.meta),
    ]);
    downloadCSV(`audit_log_${new Date().toISOString().slice(0, 10)}`, header, rows);
  };

  const exportPdf = () => {
    if (!items.length) return;
    const header = ["التوقيت", "الحدث", "المستخدم", "IP", "التفاصيل"];
    const rows = items.map((r) => [
      (r.ts || "").replace("T", " ").slice(0, 19),
      EVENT_LABELS[r.event]?.lbl?.replace(/^\S+\s/, "") || r.event,
      r.username || "—", r.ip || "—", formatMeta(r.meta).slice(0, 80),
    ]);
    downloadPDF({
      title: "سجل النشاط — Thawany",
      subtitle: `${items.length} حدث`,
      filename: `audit_log_${new Date().toISOString().slice(0, 10)}`,
      headers: header,
      rows,
      summary: [`تاريخ التصدير: ${formatKSADate(new Date())}`],
    });
  };

  return (
    <div data-testid="audit-log-page" style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div className="card" style={{ marginBottom: 14 }}>
        <h3 style={{ margin: "0 0 4px", color: "var(--brown-deep)" }}>📜 سجل النشاط</h3>
        <p style={{ margin: 0, fontSize: 12, color: "var(--muted)", lineHeight: 1.8 }}>
          يعرض كل العمليات الحساسة (دخول، حذف، تعديل صلاحيات). متاح للمدير فقط.
        </p>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end" }}>
          <div>
            <label style={{ fontSize: 11, color: "var(--muted)", display: "block", marginBottom: 4 }}>نوع الحدث</label>
            <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} data-testid="event-filter" style={{ fontSize: 12, padding: "5px 8px", minWidth: 180 }}>
              <option value="">كل الأحداث</option>
              {Object.entries(EVENT_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v.lbl}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: "var(--muted)", display: "block", marginBottom: 4 }}>اسم المستخدم</label>
            <input
              type="text"
              value={usernameFilter}
              onChange={(e) => setUsernameFilter(e.target.value)}
              placeholder="مثال: admin"
              data-testid="username-filter"
              style={{ fontSize: 12, padding: "5px 8px", minWidth: 160 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 11, color: "var(--muted)", display: "block", marginBottom: 4 }}>عدد النتائج</label>
            <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} data-testid="limit-filter" style={{ fontSize: 12, padding: "5px 8px" }}>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
              <option value={500}>500</option>
            </select>
          </div>
          <button type="button" className="btn btn-primary" onClick={load} disabled={loading} data-testid="reload-audit" style={{ fontSize: 12 }}>
            {loading ? "⏳" : "🔄 تحديث"}
          </button>
          <button type="button" className="btn btn-soft" onClick={exportCsv} disabled={!items.length} data-testid="export-audit" style={{ fontSize: 12 }}>
            📥 CSV
          </button>
          <button type="button" className="btn btn-soft" onClick={exportPdf} disabled={!items.length} data-testid="export-audit-pdf" style={{ fontSize: 12 }}>
            📄 PDF
          </button>
        </div>
      </div>

      {err && <div className="al alr" data-testid="audit-error">⚠ {err}</div>}

      <div className="card" data-testid="audit-results">
        <div style={{ marginBottom: 8, fontSize: 11, color: "var(--muted)" }}>
          عُرض {items.length.toLocaleString("en-US")} حدث
        </div>
        {loading ? (
          <div style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>⏳ جارٍ التحميل...</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: "var(--muted)", fontSize: 12 }}>لا توجد أحداث مطابقة</div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 140 }}>التوقيت</th>
                  <th style={{ width: 140 }}>الحدث</th>
                  <th>المستخدم</th>
                  <th>IP</th>
                  <th>التفاصيل</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, i) => (
                  <tr key={i} data-testid={`audit-row-${i}`}>
                    <td style={{ fontSize: 11, whiteSpace: "nowrap", direction: "ltr", fontFamily: "monospace" }}>
                      {(r.ts || "").replace("T", " ").slice(0, 19)}
                    </td>
                    <td>{eventChip(r.event)}</td>
                    <td>
                      <b>{r.username || "—"}</b>
                      {r.role && <span style={{ marginInlineStart: 6, fontSize: 10, color: "var(--muted)" }}>({r.role})</span>}
                    </td>
                    <td style={{ direction: "ltr", fontFamily: "monospace", fontSize: 11 }}>{r.ip || "—"}</td>
                    <td style={{ fontSize: 11, color: "var(--muted)" }}>{formatMeta(r.meta)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
